// src/components/Profile/Profile.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI } from '../../services/api';
import { 
  User, 
  Mail, 
  Phone, 
  Save, 
  Eye, 
  EyeOff,
  CheckCircle,
  XCircle,
  Building,
  Lock,
  Camera,
  Shield,
  Bell
} from 'lucide-react';
import { motion } from 'framer-motion';

const Profile = () => {
  const { user, token, login, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [profilePreview, setProfilePreview] = useState(user?.profilePictureUrl || '');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  // Initialize form data with user data
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setProfilePreview(user.profilePictureUrl || '');
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};

    // Basic info validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password change validation (only if any password field is filled)
    if (formData.currentPassword || formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to change password';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Password must be at least 6 characters';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleProfileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
        // You can add API call to upload profile picture here
        console.log('📸 Profile picture selected:', file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      };

      // Only include password fields if they are provided
      if (formData.currentPassword && formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      console.log('📤 Updating profile:', updateData);
      const response = await usersAPI.updateProfile(updateData);
      console.log('✅ Profile update response:', response.data);

      // ProfileController's PUT /api/Profile returns the flat ProfileResponse
      // DTO itself (id/firstName/.../companyName), not a { user: ... }
      // wrapper -- spreading "response.data.user" here was always spreading
      // undefined, silently discarding the fresh companyId/companyName/
      // userType the server just sent back and relying entirely on
      // whatever the client already had cached.
      //
      // updateUser() (from AuthContext) merges this into the live `user`
      // state AND persists it to whichever storage actually holds this
      // session, so a saved name/phone shows up immediately everywhere
      // useAuth() is read (e.g. Layout's sidebar) instead of only after
      // the next full reload.
      updateUser({
        ...response.data,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      });

      setMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      });

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      console.error('❌ Profile update error:', error);
      
      let errorMessage = 'Failed to update profile';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // Handle validation errors from server
        const serverErrors = error.response.data.errors;
        errorMessage = Object.values(serverErrors).flat().join(', ');
      }

      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const getRoleText = () => {
    switch (user?.userType) {
      case 0: return 'Super Admin';
      case 1: return 'Company Admin';
      case 2: return 'Employee';
      default: return 'User';
    }
  };

  return (
    <div className="min-h-full py-8 px-6 lg:px-8" style={{ background: 'var(--bg)' }}>
      <div className="w-full">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Panel - Profile Overview */}
          <div className="w-full lg:w-1/3 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-2xl shadow-md p-6 border border-[var(--tint-line)] h-fit">
            <div className="text-center">
              {/* Profile Picture with Rounded Design */}
              <div className="relative inline-block mb-4">
                <div className="w-32 h-32 rounded-full bg-[var(--avatar-bg)] border-4 border-white shadow-lg overflow-hidden">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center rounded-full bg-[var(--avatar-bg)]">
                      <User className="w-12 h-12 text-[var(--avatar-ink)]" />
                    </div>
                  )}
                </div>

                {/* Camera Upload Button */}
                <label className="absolute bottom-2 right-2 bg-[var(--accent)] text-white p-2 rounded-full shadow-lg cursor-pointer hover:bg-[var(--accent-strong)] transition-colors">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfileChange}
                  />
                </label>
              </div>

              <h2 className="text-xl font-bold text-gray-900">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="text-gray-600 flex items-center justify-center mt-1">
                <Mail className="w-4 h-4 mr-1" />
                {user?.email}
              </p>

              {user?.companyName && (
                <p className="text-gray-600 flex items-center justify-center mt-1">
                  <Building className="w-4 h-4 mr-1" />
                  {user.companyName}
                </p>
              )}

              {/* Role Badge */}
              <div className="mt-4 inline-block bg-[var(--accent)] text-white px-4 py-2 rounded-full text-sm font-semibold">
                {getRoleText()}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 space-y-3">
              <div className="bg-white rounded-lg p-4 border border-[var(--tint-line)] shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[var(--tint)] rounded-lg">
                    <Shield className="w-5 h-5 text-[var(--accent)]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Account Status</p>
                    <p className="text-sm font-semibold text-gray-900">Active</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-[var(--tint-line)] shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[var(--tint)] rounded-lg">
                    <Bell className="w-5 h-5 text-[var(--accent)]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Last Updated</p>
                    <p className="text-sm font-semibold text-gray-900">Just now</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Completion */}
            <div className="mt-6 bg-white rounded-lg p-4 border border-[var(--tint-line)] shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Profile Completion</span>
                <span className="text-sm font-bold text-[var(--accent)]">85%</span>
              </div>
              <div className="w-full bg-[var(--tint)] rounded-full h-2">
                <div className="bg-[var(--accent)] h-2 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
          </div>

          {/* Right Panel - Profile Form */}
          <div className="flex-1">
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-2xl shadow-md p-6 border border-[var(--tint-line)]"
            >
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
                <p className="text-gray-600 mt-2">
                  Update your personal information and manage your account security
                </p>
              </div>

              {/* Message Alert */}
              {message.text && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-lg flex items-center ${
                    message.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}
                >
                  {message.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 mr-2" />
                  )}
                  <span className="flex-1">{message.text}</span>
                  <button
                    onClick={clearMessage}
                    className="ml-4 text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </motion.div>
              )}

              {/* Personal Information Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 border-[var(--tint-line)]">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                        errors.firstName ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                        errors.lastName ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                        errors.email ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 border-[var(--tint-line)]">
                  Change Password
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                          errors.currentPassword ? 'border-red-500' : ''
                        }`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                    )}
                  </div>

                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                            errors.newPassword ? 'border-red-500' : ''
                          }`}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                          errors.confirmPassword ? 'border-red-500' : ''
                        }`}
                        placeholder="Confirm new password"
                      />
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-[var(--tint-line)]">
                <button
                  type="button"
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  onClick={() => {
                    setFormData({
                      firstName: user?.firstName || '',
                      lastName: user?.lastName || '',
                      email: user?.email || '',
                      phone: user?.phone || '',
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setErrors({});
                    setMessage({ type: '', text: '' });
                  }}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg hover:bg-[var(--accent-strong)] focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;